import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { Menu, MenuItem } from '@lifeomic/chroma-react/components/Menu';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import {
  ArrowLeft,
  Bullhorn,
  Check2,
  Code,
  Command,
  Edit2,
  SparkleRefresh,
  X,
} from '@lifeomic/chromicons';
import React, { useState } from 'react';
import { ContainerQuery } from 'react-container-query';
import { useIntl, defineMessages } from 'react-intl';
import EnforceABAC, { permissions } from '../../../../EnforceABAC';
import clsx from 'clsx';
import { useImpressionEditorContext } from './Context';
import { useActiveUserId, useMixpanel } from '../../../../../hooks';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { TextArea } from '@lifeomic/chroma-react/components/TextArea';
import { ButtonLink } from '@lifeomic/chroma-react/components/ButtonLink';
import urls from '../../../../../util/urls';
import { IconButtonLink } from '@lifeomic/chroma-react/components/IconButtonLink';
import { ViewMode } from '../Attachments/InlineAttachment';
import { ViewModePicker } from '../ViewModePicker';
import { GenerateModal } from '../Templates/GenerateModal';

// Messages for internationalization
export const messages = defineMessages({
  back: {
    id: 'phc-web-toolkit.subjectViewer.aiTools.controlBar.back',
    defaultMessage: 'Back',
  },
  close: {
    id: 'phc-web-toolkit.subjectViewer.aiTools.controlBar.close',
    defaultMessage: 'Close',
  },
  editSummary: {
    id: 'phc-web-toolkit.subjectViewer.aiTools.controlBar.editSummary',
    defaultMessage: 'Edit',
  },
  regenerate: {
    id: 'phc-web-toolkit.subjectViewer.aiTools.controlBar.regenerate',
    defaultMessage: 'Regenerate',
  },
  regenerateInProgress: {
    id: 'phc-web-toolkit.subjectViewer.aiTools.controlBar.regenerateInProgress',
    defaultMessage: 'Regenerating...',
  },
  feedback: {
    id: 'phc-web-toolkit.subjectViewer.aiTools.controlBar.feedback',
    defaultMessage: 'Feedback',
  },
  keyboardShortcuts: {
    id: 'phc-web-toolkit.subjectViewer.aiTools.controlBar.keyboardShortcuts',
    defaultMessage: 'Keyboard Shortcuts',
  },
  generateFailure: {
    id: 'phc-web-toolkit.subjectViewer.aiTools.controlBar.generateFailure',
    defaultMessage: 'Failed to regenerate content. Please try again.',
  },
  leaveFeedback: {
    id: 'phc-web-toolkit.subjectViewer.aiTools.controlBar.leaveFeedback',
    defaultMessage: 'Leave Feedback',
  },
  placeholderFeedback: {
    id: 'phc-web-toolkit.subjectViewer.aiTools.controlBar.placeholderFeedback',
    defaultMessage: 'Please provide feedback on the report',
  },
  signOff: {
    id: 'phc-web-toolkit.subjectViewer.aiTools.controlBar.signOff',
    defaultMessage: 'Sign Off',
  },
  signOffModal: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.signOffModal',
    defaultMessage: 'How would you like to sign off on this report?',
  },
  signOffMemberVisible: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.signOffMemberVisible',
    defaultMessage: 'Make visible to member',
  },
  signOffMemberVisibleDescription: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.signOffMemberVisibleDescription',
    defaultMessage:
      'This report and all referenced documents will be made available to the member.',
  },
  signOffClinicOnly: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.signOffClinicOnly',
    defaultMessage: 'Clinic only',
  },
  signOffClinicOnlyDescription: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.signOffClinicOnlyDescription',
    defaultMessage:
      'This report will be exported to Elation as a visit note, but will not be visible to the member.',
  },
  cancel: {
    id: 'phc-web-toolkit.subjectViewer.aiTools.controlBar.cancel',
    defaultMessage: 'Cancel',
  },
  manageTemplate: {
    id: 'phc-web-toolkit.subjectViewer.aiTools.controlBar.manageTemplate',
    defaultMessage: 'Manage Template',
  },
});

// Keyboard shortcuts messages
export const keyboardShortcuts = defineMessages({
  meta: {
    id: 'phc-web-toolkit.subjectViewer.aiTools.controlBar.keyboardShortcuts.meta',
    defaultMessage: '⌘ = command/meta key, ⇧ = shift key',
  },
  startEditing: {
    id: 'phc-web-toolkit.subjectViewer.aiTools.controlBar.keyboardShortcuts.startEditing',
    defaultMessage: '⌘ + e: Start editing',
  },
  whenEditing: {
    id: 'phc-web-toolkit.subjectViewer.aiTools.controlBar.keyboardShortcuts.whenEditing',
    defaultMessage: 'When editing:',
  },
  saveAndExitEditMode: {
    id: 'phc-web-toolkit.subjectViewer.aiTools.controlBar.keyboardShortcuts.saveAndExitEditMode',
    defaultMessage: '⌘ + s: Save and exit edit mode',
  },
  saveAndContinueEditing: {
    id: 'phc-web-toolkit.subjectViewer.aiTools.controlBar.keyboardShortcuts.saveAndContinueEditing',
    defaultMessage: '⌘ + ⇧ + s: Save and continue editing',
  },
  bold: {
    id: 'phc-web-toolkit.subjectViewer.aiTools.controlBar.keyboardShortcuts.bold',
    defaultMessage: '⌘ + b: Bold',
  },
  italic: {
    id: 'phc-web-toolkit.subjectViewer.aiTools.controlBar.keyboardShortcuts.italic',
    defaultMessage: '⌘ + i: Italic',
  },
  underline: {
    id: 'phc-web-toolkit.subjectViewer.aiTools.controlBar.keyboardShortcuts.underline',
    defaultMessage: '⌘ + u: Underline',
  },
  leaveFeedback: {
    id: 'phc-web-toolkit.subjectViewer.aiTools.controlBar.leaveFeedback',
    defaultMessage: 'Leave Feedback',
  },
  placeholderFeedback: {
    id: 'phc-web-toolkit.subjectViewer.aiTools.controlBar.placeholderFeedback',
    defaultMessage: 'Please provide feedback on the report',
  },
});

const query = {
  isNarrow: {
    maxWidth: 740,
  },
};

const useStyles = makeStyles(theme => ({
  header: {
    backgroundColor: theme.palette.common.white,
    borderCollapse: 'collapse',
    boxSizing: 'border-box',
    padding: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    justifyContent: 'space-between',
    maxHeight: theme.pxToRem(60),
    '& button': {
      paddingLeft: theme.pxToRem(14),
      paddingRight: theme.pxToRem(14),
    },
  },

  editButton: {
    '&:disabled, &[disabled]': {
      backgroundColor: theme.palette.primary.main,
      borderRadius: theme.pxToRem(18),
      color: theme.palette.common.white,
      cursor: 'not-allowed',
      opacity: 1,
      minHeight: theme.pxToRem(35),
      paddingTop: theme.pxToRem(6),
      paddingBottom: theme.pxToRem(6),
    },
  },
  editIconButton: {
    '&:disabled, &[disabled]': {
      paddingTop: theme.pxToRem(12),
      paddingBottom: theme.pxToRem(12),
    },
  },
  signOffOption: {
    padding: theme.pxToRem(12),
    border: `${theme.pxToRem(1)} solid ${theme.palette.graphite[200]}`,
    borderRadius: theme.pxToRem(4),
    backgroundColor: theme.palette.common.white,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.primary[50],
    },
  },
  signOffOptionSelected: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary[50],
  },
  signOffContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  signOffRadio: {
    marginRight: theme.spacing(1),
  },
}));

export interface ControlBarProps {
  /** Optional callback to close the component */
  onClose?: () => void;
  onSignOff: (signOffType?: 'member' | 'clinic') => Promise<void>;
  viewMode: ViewMode;
  onSelectViewMode: (newMode: ViewMode) => void;
  isGenerating: boolean;
}

export const ControlBar: React.FC<ControlBarProps> = ({
  onClose,
  onSignOff,
  viewMode,
  onSelectViewMode,
  isGenerating,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles();
  const { activeImpression, isEditing, setIsEditing, sendAgentFeedback } =
    useImpressionEditorContext();
  const userId = useActiveUserId();
  const mixpanel = useMixpanel();

  const traceId = activeImpression?.traceId;

  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [comment, setComment] = useState('');
  const [isSendingFeedback, setIsSendingFeedback] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const sendFeedback = async () => {
    if (!traceId || !comment) {
      return;
    }
    setIsSendingFeedback(true);

    await sendAgentFeedback({
      agentName: 'template-agent',
      traceId,
      feedback: `${userId} a template report: ${comment}`,
    }).catch(() => {
      setIsSendingFeedback(false);
    });

    setComment('');
    setShowFeedbackModal(false);
    mixpanel.track('template_report_feedback_sent', {
      impressionType: activeImpression?.impressionType,
      impressionId: activeImpression?.impression.id,
    });

    setIsSendingFeedback(false);
  };

  const [isSigningOff, setIsSigningOff] = useState(false);
  const [signOffType, setSignOffType] = useState<'member' | 'clinic' | null>(
    null,
  );

  const [isTriggeringRegeneration, setIsTriggeringRegeneration] =
    useState(false);

  return (
    <ContainerQuery query={query}>
      {params => (
        <Box className={classes.header} fullWidth>
          <Modal
            isOpen={showFeedbackModal}
            onDismiss={() => setShowFeedbackModal(false)}
            title={formatMessage(messages.leaveFeedback)}
            size={1}
            fullWidth
            actions={
              <>
                <Button
                  variant="outlined"
                  onClick={() => sendFeedback()}
                  disabled={!comment || isSendingFeedback}
                >
                  {formatMessage(messages.leaveFeedback)}
                </Button>
              </>
            }
          >
            <TextArea
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus={showFeedbackModal}
              aria-label="Feedback"
              placeholder={formatMessage(messages.placeholderFeedback)}
              onChange={content => setComment(content.target.value)}
              fullWidth
              rows={10}
            />
          </Modal>
          <Modal
            isOpen={isSigningOff}
            onDismiss={() => {
              setIsSigningOff(false);
              setSignOffType(null);
            }}
            title={formatMessage(messages.signOff)}
            size={1}
            actions={[
              <Button
                key="cancel"
                variant="outlined"
                onClick={() => {
                  setIsSigningOff(false);
                  setSignOffType(null);
                }}
              >
                {formatMessage(messages.cancel)}
              </Button>,
              <Button
                key="signOff"
                onClick={async () => {
                  if (!signOffType) return;
                  setIsSaving(true);
                  setIsSigningOff(false);
                  try {
                    await onSignOff(signOffType);
                  } finally {
                    setIsSaving(false);
                    setSignOffType(null);
                  }
                }}
                disabled={isSaving || !signOffType}
              >
                {formatMessage(messages.signOff)}
              </Button>,
            ]}
          >
            <Box direction="column">
              <Text size="body">{formatMessage(messages.signOffModal)}</Text>

              <Box
                direction="column"
                gap={2}
                className={classes.signOffContainer}
              >
                <Box
                  className={clsx(
                    classes.signOffOption,
                    signOffType === 'member' && classes.signOffOptionSelected,
                  )}
                  direction="row"
                  align="center"
                  onClick={() => setSignOffType('member')}
                >
                  <input
                    type="radio"
                    id="member-visible"
                    name="sign-off-type"
                    checked={signOffType === 'member'}
                    onChange={() => setSignOffType('member')}
                    className={classes.signOffRadio}
                  />
                  <Box direction="column">
                    <Text weight="bold">
                      {formatMessage(messages.signOffMemberVisible)}
                    </Text>
                    <Text size="caption">
                      {formatMessage(messages.signOffMemberVisibleDescription)}
                    </Text>
                  </Box>
                </Box>

                <Box
                  className={clsx(
                    classes.signOffOption,
                    signOffType === 'clinic' && classes.signOffOptionSelected,
                  )}
                  direction="row"
                  align="center"
                  onClick={() => setSignOffType('clinic')}
                >
                  <input
                    type="radio"
                    id="clinic-only"
                    name="sign-off-type"
                    checked={signOffType === 'clinic'}
                    onChange={() => setSignOffType('clinic')}
                    className={classes.signOffRadio}
                  />
                  <Box direction="column">
                    <Text weight="bold">
                      {formatMessage(messages.signOffClinicOnly)}
                    </Text>
                    <Text size="caption">
                      {formatMessage(messages.signOffClinicOnlyDescription)}
                    </Text>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Modal>
          <GenerateModal
            isOpen={isTriggeringRegeneration}
            onDismiss={() => setIsTriggeringRegeneration(false)}
            templateId={activeImpression.templateId}
            activeImpression={activeImpression}
          />
          <Box>
            {!params['isNarrow'] ? (
              <>
                <Button
                  disabled={isEditing}
                  variant="text"
                  icon={ArrowLeft}
                  onClick={onClose}
                >
                  {formatMessage(messages.back)}
                </Button>
                {activeImpression?.status !== 'completed' && (
                  <EnforceABAC permission={permissions.updateData}>
                    <Button
                      className={classes.editButton}
                      onClick={() => setIsEditing(true)}
                      variant="text"
                      icon={Edit2}
                      disabled={isEditing}
                    >
                      {formatMessage(messages.editSummary)}
                    </Button>
                  </EnforceABAC>
                )}

                <Button
                  variant="text"
                  icon={SparkleRefresh}
                  disabled={isGenerating}
                  onClick={() => setIsTriggeringRegeneration(true)}
                >
                  {isGenerating
                    ? formatMessage(messages.regenerateInProgress)
                    : formatMessage(messages.regenerate)}
                </Button>

                {traceId && (
                  <Button
                    variant="text"
                    icon={Bullhorn}
                    onClick={() => setShowFeedbackModal(true)}
                  >
                    {formatMessage(messages.feedback)}
                  </Button>
                )}
                <ButtonLink
                  variant="text"
                  icon={Code}
                  to={urls.app.workflows.reportTemplates(
                    activeImpression?.templateId,
                  )}
                  target={isEditing ? '_blank' : '_self'}
                >
                  {formatMessage(messages.manageTemplate)}
                </ButtonLink>

                <Menu
                  aria-label={formatMessage(messages.keyboardShortcuts)}
                  anchorElement={
                    <Button variant="text" icon={Command}></Button>
                  }
                  items={[
                    <MenuItem key="keyboardShortcutsDescription">
                      <Box direction="column">
                        <Text size="caption" weight="bold">
                          {formatMessage(messages.keyboardShortcuts)}
                        </Text>
                        <Text size="caption">
                          {Object.keys(keyboardShortcuts).map(key => (
                            <Text key={key} size="caption">
                              {formatMessage(
                                keyboardShortcuts[
                                  key as keyof typeof keyboardShortcuts
                                ],
                              )}
                            </Text>
                          ))}
                        </Text>
                      </Box>
                    </MenuItem>,
                  ]}
                />
                {activeImpression?.status !== 'completed' && (
                  <Button
                    variant="text"
                    icon={Check2}
                    onClick={() => setIsSigningOff(true)}
                    disabled={isSaving}
                  >
                    {formatMessage(messages.signOff)}
                  </Button>
                )}
              </>
            ) : (
              <>
                {activeImpression?.status !== 'completed' && (
                  <EnforceABAC permission={permissions.updateData}>
                    <Tooltip title={formatMessage(messages.editSummary)}>
                      <IconButton
                        aria-label={formatMessage(messages.editSummary)}
                        className={clsx(
                          classes.editButton,
                          classes.editIconButton,
                        )}
                        onClick={() => setIsEditing(true)}
                        color="primary"
                        icon={Edit2}
                        size={0}
                        disabled={isEditing}
                      />
                    </Tooltip>
                  </EnforceABAC>
                )}

                <Tooltip
                  title={
                    isGenerating
                      ? formatMessage(messages.regenerateInProgress)
                      : formatMessage(messages.regenerate)
                  }
                >
                  <Box>
                    <IconButton
                      aria-label={formatMessage(messages.regenerate)}
                      onClick={() => setIsTriggeringRegeneration(true)}
                      disabled={isGenerating}
                      color="primary"
                      icon={SparkleRefresh}
                      size={0}
                    />
                  </Box>
                </Tooltip>

                {traceId && (
                  <Tooltip title={formatMessage(messages.feedback)}>
                    <IconButton
                      aria-label={formatMessage(messages.feedback)}
                      onClick={() => setShowFeedbackModal(true)}
                      color="primary"
                      icon={Bullhorn}
                      size={0}
                    />
                  </Tooltip>
                )}
                <Tooltip title={formatMessage(messages.manageTemplate)}>
                  <IconButtonLink
                    aria-label={formatMessage(messages.manageTemplate)}
                    color="primary"
                    icon={Code}
                    size={0}
                    to={urls.app.workflows.reportTemplates(
                      activeImpression?.templateId,
                    )}
                    target={isEditing ? '_blank' : 'self'}
                  />
                </Tooltip>

                <Tooltip title={formatMessage(messages.keyboardShortcuts)}>
                  <IconButton
                    aria-label={formatMessage(messages.keyboardShortcuts)}
                    color="primary"
                    icon={Command}
                    size={0}
                  />
                </Tooltip>
                {activeImpression?.status !== 'completed' && (
                  <Tooltip title={formatMessage(messages.signOff)}>
                    <IconButton
                      aria-label={formatMessage(messages.signOff)}
                      color="primary"
                      icon={Check2}
                      size={0}
                      onClick={() => setIsSigningOff(true)}
                    />
                  </Tooltip>
                )}
              </>
            )}
          </Box>
          <Box>
            <ViewModePicker mode={viewMode} onSelect={onSelectViewMode} />
            {onClose && (
              <Tooltip title={formatMessage(messages.close)}>
                <IconButton
                  onClick={onClose}
                  disabled={isEditing}
                  aria-label={formatMessage(messages.close)}
                  color="primary"
                  icon={X}
                  size={0}
                />
              </Tooltip>
            )}
          </Box>
        </Box>
      )}
    </ContainerQuery>
  );
};
